/* ════════════ THE REFRACTION ════════════
   The prism page's atmosphere: thin beams cross the dark, hit a
   point, and split into a small spectrum that fans out and fades.
   White light in, everything you are out. Slow, quiet, everywhere. */

import { onPage } from "./router";

const REDUCED = matchMedia("(prefers-reduced-motion: reduce)").matches;

function refraction(signal: AbortSignal) {
  const c = document.querySelector<HTMLCanvasElement>("#refraction")!;
  const ctx = c.getContext("2d")!;

  const WHITE = "#eef6ff";
  const GREY = "#9aa0a6";
  const SPECTRUM = ["#ff5a5a", "#ffb84d", "#ffe96b", "#6bff9e", "#62b8ff", "#b07cff"];

  type Split = {
    x: number; // apex: where the beam hits
    y: number;
    ang: number; // incoming beam direction
    beam: number; // beam length
    ray: number; // spectral ray length
    spread: number; // total fan angle
    t: number; // age in frames
    life: number;
    hot: boolean;
  };
  let splits: Split[] = [];

  const spawn = (): Split => {
    const hot = Math.random() < 0.22;
    return {
      x: c.width * (0.1 + Math.random() * 0.8),
      y: c.height * (0.1 + Math.random() * 0.8),
      ang: Math.random() * Math.PI * 2,
      beam: 70 + Math.random() * 110,
      ray: 40 + Math.random() * 70,
      spread: 0.5 + Math.random() * 0.5,
      t: 0,
      life: 320 + Math.random() * 320,
      hot,
    };
  };

  const seed = () => {
    c.width = innerWidth;
    c.height = innerHeight;
    const count = Math.min(11, Math.floor((c.width * c.height) / 160000));
    splits = Array.from({ length: count }, () => {
      const s = spawn();
      s.t = Math.random() * s.life; // desync
      return s;
    });
  };
  seed();
  addEventListener("resize", seed, { signal });

  const ray = (x: number, y: number, ang: number, len: number, col: string, a: number) => {
    const ex = x + Math.cos(ang) * len;
    const ey = y + Math.sin(ang) * len;
    const g = ctx.createLinearGradient(x, y, ex, ey);
    g.addColorStop(0, col);
    g.addColorStop(1, "transparent");
    ctx.globalAlpha = a;
    ctx.strokeStyle = g;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(ex, ey);
    ctx.stroke();
  };

  const draw = () => {
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.lineWidth = 1;
    for (const s of splits) {
      const p = s.t / s.life;
      const env = p < 0.2 ? p / 0.2 : p > 0.65 ? (1 - p) / 0.35 : 1;
      const base = env * (s.hot ? 0.45 : 0.18);
      // incoming beam, arriving at the apex
      ray(s.x, s.y, s.ang + Math.PI, s.beam, s.hot ? WHITE : GREY, base);
      // the spectrum: rays grow out over the first 35% of life
      const grow = Math.min(1, p / 0.35);
      const n = SPECTRUM.length;
      for (let i = 0; i < n; i++) {
        const a = s.ang - s.spread / 2 + (s.spread * i) / (n - 1);
        ray(s.x, s.y, a, s.ray * grow, SPECTRUM[i]!, base * 0.8);
      }
      // the apex itself, a single bright grain
      ctx.globalAlpha = base;
      ctx.fillStyle = s.hot ? WHITE : GREY;
      ctx.fillRect(s.x - 1, s.y - 1, 2, 2);
    }
    ctx.globalAlpha = 1;
  };

  if (REDUCED) {
    draw();
    return;
  }
  const tick = () => {
    if (signal.aborted) return;
    for (let i = 0; i < splits.length; i++) {
      const s = splits[i]!;
      s.t++;
      if (s.t >= s.life) splits[i] = spawn();
    }
    draw();
    requestAnimationFrame(tick);
  };
  tick();
}

onPage(refraction);
